/**
 * อ่านเมนูจากแชท: POST /api/v1/conversations/:id/menu-read (api/app/ai/menu_read.py — the POS sale page's
 * "วางเมนูจากแชท" in the chat). Staff-pressed only. Mock mode reads the demo texts with the local matcher and returns
 * a fixed answer for the demo menu sheet (src/api/mockData.ts, chat c101).
 */
import { ApiError } from './client'
import { reportUnauthorized } from './http'
import { getApi, useMock } from './index'
import { MockChatApi } from './mock'
import { getPosApi } from './pos'
import type { PosMenu } from './pos/types'
import type { ChatMessage } from './types'
import { detectMenus, mixHint, packLine, rowsFrom, suggestPack, tally } from '../lib/menuRead'

export interface MenuReadItem {
  menu_id: number
  /** "P5" / "S4" — null for a dish without a code. */
  p: string | null
  name: string
  qty: number
  /** false = the AI saw no number (1 is a guess). */
  qty_explicit: boolean
  /** Where it came in the customer's text / picture (0 = first). */
  order: number
  /** Picture only: [ymin, xmin, ymax, xmax] in 0–1000 of the whole picture. */
  box: [number, number, number, number] | null
  /** Snacks do not count towards the pack. */
  snack: boolean
}

/**
 * "คละ" + the customer's limit (round 2): meat types for "สุ่มเมนูที่เหลือ" — `only` ("คละเอาแต่ไก่"), `exclude`
 * ("ไม่เอาหมู"), `said` = the words it came from. A hint for the chips; nothing is filled without the staff.
 */
export interface MixHint {
  mix: boolean
  only: string[]
  exclude: string[]
  said: string
}

export interface MenuReadPack {
  id: number
  name: string
  meals: number
  price?: number | null
}

export interface MenuReadResult {
  items: MenuReadItem[]
  /** Names the AI could not match ("P7." = a code the customer typed that the AI left out). */
  unmatched: string[]
  warn: string[]
  customer_total: number | null
  /** The customer lets the shop pick the rest ("คละ"). */
  mix_rest: boolean
  mix_note: string
  /** Absent on an older API. */
  mix_hint?: MixHint
  summary: { level: 'ok' | 'warn' | 'none'; text: string; menus: number; boxes: number; snacks: number; limit: number | null; diff: number | null }
  /** What the customer said about the pack + the catalog's set menus that fit (never invented). */
  pack: { stated: { boxes: number | null; text: string | null } | null; suggested: MenuReadPack | null; candidates: MenuReadPack[] }
  source: { kind: 'text' | 'image'; message_ids: string[] }
  usage: { model: string; fallback: boolean; tokens_in: number; tokens_out: number; ms: number; cost_thb: number | null }
}

export interface MenuReadBody {
  message_ids: string[]
  note?: string
  set_menu_id?: number | null
  /** Continuing an open pack: the meals left in it. */
  pack_limit?: number | null
  pack_name?: string | null
}

export interface MenuReadApi {
  read(convId: string, body: MenuReadBody): Promise<MenuReadResult>
  /** The owner's switch "อ่านเมนูจากแชท" (Config › AI, `menu_read`; absent on an older API = on). */
  enabled(): Promise<boolean>
}

export class HttpMenuReadApi implements MenuReadApi {
  constructor(
    private readonly base: string,
    /** The API gives the model 55 s. */
    private readonly timeoutMs = 75000,
    private readonly fetchImpl: typeof fetch = (...a) => fetch(...a),
  ) {}

  async read(convId: string, body: MenuReadBody): Promise<MenuReadResult> {
    const url = `${this.base.replace(/\/+$/, '')}/api/v1/conversations/${encodeURIComponent(convId)}/menu-read`
    const ctrl = new AbortController()
    const timer = setTimeout(() => ctrl.abort(), this.timeoutMs)
    try {
      const res = await this.fetchImpl(url, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        credentials: 'same-origin',
        signal: ctrl.signal,
      })
      if (res.status === 401) reportUnauthorized()
      if (!res.ok) {
        let message = `อ่านเมนูไม่สำเร็จ (HTTP ${res.status})`
        try {
          const j = (await res.json()) as { detail?: unknown }
          if (typeof j.detail === 'string') message = j.detail
        } catch {
          /* body not JSON */
        }
        throw new ApiError(res.status, message)
      }
      return (await res.json()) as MenuReadResult
    } catch (e) {
      if (e instanceof ApiError) throw e
      if (ctrl.signal.aborted) throw new ApiError(0, 'AI ตอบช้าเกินไป ลองใหม่อีกครั้ง')
      throw new ApiError(0, 'ต่อเซิร์ฟเวอร์ไม่ได้ ลองใหม่อีกครั้ง')
    } finally {
      clearTimeout(timer)
    }
  }

  async enabled(): Promise<boolean> {
    const url = `${this.base.replace(/\/+$/, '')}/api/v1/ai/settings`
    const res = await this.fetchImpl(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin', cache: 'no-store' })
    if (res.status === 401) reportUnauthorized()
    if (!res.ok) throw new ApiError(res.status, `HTTP ${res.status}`)
    return ((await res.json()) as { menu_read?: boolean }).menu_read !== false
  }
}

// ---------------------------------------------------------------- mock

/** The demo menu sheet (mockData.ts MOCK_MENU_SHEET): ticks with the number of boxes written beside them. */
const SHEET_MARKS: [string, number, number][] = [
  ['P1', 5, 0],
  ['P2', 5, 1],
  ['P3', 4, 2],
  ['P5', 3, 4],
  ['P7', 4, 6],
  ['P8', 0, 7], // ticked, no number written: 1 box, qty_explicit false
]
const sheetBox = (row: number): [number, number, number, number] => [
  Math.round(((132 + row * 80) / 800) * 1000),
  30,
  Math.round(((132 + row * 80 + 70) / 800) * 1000),
  970,
]

const QTY_IN_LINE = /(?:x|×|\*)\s*(\d{1,3})|(\d{1,3})\s*(?:กล่อง|ถุง|แพ็ค|แพ็ก|ที่|ชุด)|(?:^|\s)(\d{1,3})\s*(?:ค่ะ|คะ|ครับ|นะ)?\s*$/i

export class MockMenuReadApi implements MenuReadApi {
  calls: { convId: string; body: MenuReadBody }[] = []

  constructor(
    private readonly latencyMs = 900,
    private readonly messages: (convId: string, ids: string[]) => ChatMessage[] = (convId, ids) => {
      const api = getApi()
      const list = api instanceof MockChatApi ? (api.db.threads.get(convId) ?? []) : []
      return list.filter((x): x is ChatMessage => x.kind === 'message' && ids.includes(x.id))
    },
  ) {}

  async enabled(): Promise<boolean> {
    return (await getApi().getAiConfig()).settings.menu_read !== false
  }

  async read(convId: string, body: MenuReadBody): Promise<MenuReadResult> {
    this.calls.push({ convId, body })
    if (this.latencyMs) await new Promise((r) => setTimeout(r, this.latencyMs))
    if (!(await this.enabled().catch(() => true))) throw new ApiError(409, 'เจ้าของปิด "อ่านเมนูจากแชท" ไว้ในหน้าตั้งค่า › AI')
    const cat = await getPosApi().catalog()
    const msgs = this.messages(convId, body.message_ids)
    const image = msgs.find((m) => m.type === 'image')
    const texts = msgs.filter((m) => m.type !== 'image').map((m) => m.text ?? '')
    const byCode = new Map(cat.menus.map((m) => [/^([A-Z]{1,3}\d+)\./.exec(m.name)?.[1] ?? '', m] as const))
    const item = (m: PosMenu, qty: number, explicit: boolean, order: number, box: MenuReadItem['box'] = null): MenuReadItem => ({
      menu_id: m.id,
      p: /^([A-Z]{1,3}\d+)\./.exec(m.name)?.[1] ?? null,
      name: m.name,
      qty,
      qty_explicit: explicit,
      order,
      box,
      snack: m.type === 'snacks',
    })
    const items: MenuReadItem[] = []
    const unmatched: string[] = []
    let total: number | null = null
    let packText: string | null = null
    if (image) {
      for (const [code, qty, row] of SHEET_MARKS) {
        const m = byCode.get(code)
        if (m) items.push(item(m, qty || 1, qty > 0, items.length, sheetBox(row)))
      }
      for (const line of [...texts, body.note ?? ''].join('\n').split(/\n+/)) {
        if (!/แพ็ก|แพ็ค|pro\s*\d|ทดลอง/i.test(line)) continue
        packText = line.trim().slice(0, 80)
        const n = /(\d{1,3})\s*(?:กล่อง|มื้อ)/.exec(line)
        total = n ? Number(n[1]) : total
      }
    } else {
      for (const line of [...texts, body.note ?? ''].join('\n').split(/\n+/)) {
        const q = QTY_IN_LINE.exec(line)
        const qty = q ? Number(q[1] ?? q[2] ?? q[3]) : 1
        if (/แพ็ก|แพ็ค|pro\s*\d|ทดลอง/i.test(line)) {
          packText = line.trim().slice(0, 80)
          const n = /(\d{1,3})\s*(?:กล่อง|มื้อ)/.exec(line)
          total = n ? Number(n[1]) : total
          continue
        }
        const d = detectMenus(line, cat.menus)
        const m = cat.menus.find((x) => x.id === d.menuIds[0])
        if (m && !items.some((i) => i.menu_id === m.id)) items.push(item(m, qty, !!q, items.length))
        else if (!m && q && line.trim()) unmatched.push(line.replace(QTY_IN_LINE, '').trim())
      }
    }
    const set = body.set_menu_id ? cat.set_menus.find((s) => s.id === body.set_menu_id) : null
    const limit = set?.meals ?? body.pack_limit ?? null
    const t = tally(rowsFrom(items))
    const line = packLine(t, limit)
    const warn: string[] = []
    if (total !== null && total !== t.meals) warn.push(`ลูกค้าบอกยอดรวม ${total} กล่อง แต่รายการนับได้ ${t.meals} กล่อง — ตรวจกับลูกค้า`)
    const mix = /คละ/.test(texts.join(' '))
    return {
      items,
      unmatched,
      warn,
      customer_total: total,
      mix_rest: mix,
      mix_note: '',
      mix_hint: mixHint([...texts, body.note ?? ''].join('\n'), mix),
      summary: {
        level: line.level,
        text: line.text,
        menus: t.menus,
        boxes: t.meals,
        snacks: t.snacks,
        limit,
        diff: limit ? limit - t.meals : null,
      },
      pack: suggestPack(cat.set_menus, total, packText),
      source: { kind: image ? 'image' : 'text', message_ids: msgs.map((m) => m.id) },
      usage: { model: 'mock', fallback: false, tokens_in: 0, tokens_out: 0, ms: this.latencyMs, cost_thb: null },
    }
  }
}

let current: MenuReadApi | null = null

export function getMenuReadApi(): MenuReadApi {
  if (!current) {
    const base = (import.meta.env.VITE_API_BASE ?? '').trim()
    current = useMock ? new MockMenuReadApi() : new HttpMenuReadApi(base)
  }
  return current
}

/** Tests inject their own backend. */
export function setMenuReadApi(api: MenuReadApi | null): void {
  current = api
}
